import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../../core/services/settings.service';
import { NotificationService } from '../../../core/services/notification.service';
import { strongPasswordValidator } from '../../../core/validators/password.validator';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-security-settings',
  imports: [CommonModule, FormsModule],
  templateUrl: './security-settings.html',
  styleUrl: './security-settings.scss',
})
export class SecuritySettings {
  showPasswordSection = false;
  isChangingPassword = false;

  teamMfaEnabled = false;
  isTeamMfaLoading = false;
  isSavingTeamMfa = false;

  passwordRotationDays: number | null = null;
  isPasswordRotationLoading = false;
  isSavingPasswordRotation = false;

  readonly activeRole = localStorage.getItem('userRole') ?? 'DOCTOR';
  get isManager(): boolean {
    return this.activeRole === 'MANAGER';
  }

  passwordRequest = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  constructor(
    private settingsService: SettingsService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.loadTeamMfaSetting();
  }

  private loadTeamMfaSetting(): void {
    this.isTeamMfaLoading = true;
    this.settingsService.getTeamMfaSetting().subscribe({
      next: (dto) => {
        this.teamMfaEnabled = dto.teamMfaEnabled;
        this.passwordRotationDays = dto.passwordRotationDays ?? null;
        this.isTeamMfaLoading = false;
      },
      error: () => {
        this.isTeamMfaLoading = false;
      },
    });
  }

  isPasswordRotationValid(): boolean {
    const days = this.passwordRotationDays ?? 0;
    // Allow 0 (no rotation) or 30-90
    return days === 0 || (days >= 30 && days <= 90);
  }

  saveTeamMfaSetting(): void {
    const days = this.passwordRotationDays ?? 0;

    if (days > 0 && days < 30) {
      this.notificationService.error(
        'Password rotation policy must be at least 30 days (or 0 to disable).',
      );
      return;
    }

    if (days > 90) {
      this.notificationService.error('Password rotation policy cannot exceed 90 days.');
      return;
    }

    this.isSavingTeamMfa = true;
    this.settingsService
      .updateTeamMfaSetting({
        teamMfaEnabled: this.teamMfaEnabled,
        passwordRotationDays: this.passwordRotationDays ?? undefined,
      })
      .subscribe({
        next: (dto) => {
          this.teamMfaEnabled = dto.teamMfaEnabled;
          this.passwordRotationDays = dto.passwordRotationDays ?? null;
          this.notificationService.success('Security policies updated.');
          this.isSavingTeamMfa = false;
        },
        error: (error) => {
          this.notificationService.error(
            'Failed to update security policies: ' +
              (error?.error?.message || error.message || 'Unknown error'),
          );
          this.isSavingTeamMfa = false;
        },
      });
  }

  changePassword(): void {
    if (
      !this.passwordRequest.currentPassword ||
      !this.passwordRequest.newPassword ||
      !this.passwordRequest.confirmPassword
    ) {
      this.notificationService.error('All password fields are required.');
      return;
    }

    if (this.passwordRequest.newPassword !== this.passwordRequest.confirmPassword) {
      this.notificationService.error('New password and confirmation do not match.');
      return;
    }

    // Validate new password strength
    const ctrl = new FormControl(this.passwordRequest.newPassword);
    const strengthErrors = strongPasswordValidator()(ctrl);
    if (strengthErrors) {
      const messages: string[] = [];
      if (strengthErrors['minlength']) messages.push('at least 8 characters');
      if (strengthErrors['missingUppercase']) messages.push('an uppercase letter');
      if (strengthErrors['missingLowercase']) messages.push('a lowercase letter');
      if (strengthErrors['missingDigit']) messages.push('a digit');
      if (strengthErrors['missingSpecial']) messages.push('a special character');
      this.notificationService.error('Password must contain: ' + messages.join(', ') + '.');
      return;
    }

    this.isChangingPassword = true;

    this.settingsService.changePassword(this.passwordRequest).subscribe({
      next: () => {
        this.notificationService.success('Password updated successfully.');
        this.passwordRequest = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        };
        this.showPasswordSection = false;
        this.isChangingPassword = false;
      },
      error: (error) => {
        this.notificationService.error(
          'Failed to change password: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
        this.isChangingPassword = false;
      },
    });
  }
}
